import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { getBlog, getBlogs } from '../lib/api';
import { formatDate } from '../lib/utils';
import { ArrowLeft, ArrowRight, Calendar, Clock } from 'lucide-react';

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const blogRes = await getBlog(slug);
        setBlog(blogRes.data);

        // Get related blogs
        const blogsRes = await getBlogs();
        setRelatedBlogs(blogsRes.data.filter(b => b.slug !== slug).slice(0, 3));
      } catch (error) {
        console.error('Error fetching blog:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-32 min-h-screen bg-[#F7F6F3]">
        <div className="container-custom">
          <div className="skeleton h-96 mb-8" />
          <div className="skeleton h-8 w-3/4 mb-4" />
          <div className="skeleton h-64" />
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="pt-32 min-h-screen">
        <div className="container-custom text-center py-20">
          <h1 className="heading-lg mb-4">Blog Post Not Found</h1>
          <Link to="/blog" className="btn-primary">Back to Blog</Link>
        </div>
      </div>
    );
  }

  // Blog Article Schema
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blog.title,
    description: blog.excerpt || blog.content?.substring(0, 160),
    image: blog.image,
    datePublished: blog.created_at,
    dateModified: blog.updated_at || blog.created_at,
    author: {
      '@type': 'Organization',
      name: 'Passion of Aroma',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Passion of Aroma',
      logo: {
        '@type': 'ImageObject',
        url: 'https://customer-assets.emergentagent.com/job_d0f143f0-1699-422f-b890-814109ebd54c/artifacts/upst1ask_Logo-Passion-of-Aroma-03-1.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://passionofaroma.com/blog/${blog.slug}`,
    },
  };

  // Estimate reading time (avg 200 words per minute)
  const wordCount = blog.content?.split(/\s+/).length || 0;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <>
      <SEOHead
        title={blog.meta_title || blog.title}
        description={blog.meta_description || blog.excerpt || blog.content?.substring(0, 160)}
        canonical={`https://passionofaroma.com/blog/${blog.slug}`}
        ogImage={blog.image}
        ogType="article"
        schemaData={blogSchema}
      />

      {/* Hero with Featured Image */}
      <section className="pt-32 pb-16 bg-[#0B1F3B] relative" data-testid="blog-detail-hero">
        {blog.image && (
          <>
            <div className="absolute inset-0 opacity-30">
              <img src={blog.image} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3B] via-[#0B1F3B]/80 to-[#0B1F3B]/60" />
          </>
        )}
        <div className="container-custom relative">
          <Breadcrumbs items={[
            { label: 'Blog', href: '/blog' },
            { label: blog.title },
          ]} />
          <div className="mt-6 max-w-3xl">
            <div className="flex items-center gap-4 text-gray-400 text-sm mb-4">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(blog.created_at)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {readingTime} min read
              </span>
            </div>
            <h1 className="heading-xl text-white mb-4">{blog.title}</h1>
            {blog.excerpt && (
              <p className="text-gray-300 text-lg">{blog.excerpt}</p>
            )}
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-16 bg-white" data-testid="blog-detail-content">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            {/* Blog Body */}
            <article className="prose prose-lg max-w-none">
              <div 
                className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                style={{ fontSize: '1.125rem', lineHeight: '1.8' }}
              >
                {blog.content}
              </div>
            </article>

            {/* Back to Blog */}
            <div className="mt-12 pt-8 border-t border-gray-100">
              <Link 
                to="/blog" 
                className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#0B1F3B] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to All Articles
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedBlogs.length > 0 && (
        <section className="py-16 bg-[#F7F6F3]" data-testid="blog-related">
          <div className="container-custom">
            <h2 className="heading-md mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {relatedBlogs.map((relatedBlog) => (
                <article
                  key={relatedBlog.id}
                  className="bg-white group"
                >
                  {relatedBlog.image && (
                    <Link to={`/blog/${relatedBlog.slug}`} className="block overflow-hidden">
                      <img
                        src={relatedBlog.image}
                        alt={relatedBlog.title}
                        className="w-full aspect-video object-cover image-hover"
                      />
                    </Link>
                  )}
                  <div className="p-6">
                    <time className="text-xs text-gray-400 uppercase tracking-widest">
                      {formatDate(relatedBlog.created_at)}
                    </time>
                    <h3 className="heading-sm mt-2 mb-3 group-hover:text-[#D4AF37] transition-colors">
                      <Link to={`/blog/${relatedBlog.slug}`}>{relatedBlog.title}</Link>
                    </h3>
                    <Link
                      to={`/blog/${relatedBlog.slug}`}
                      className="inline-flex items-center gap-2 text-[#D4AF37] text-sm uppercase tracking-widest font-medium"
                    >
                      Read More
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
